import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Badge,
  Alert,
  Table,
  Input,
} from 'reactstrap';
import ConfirmModal from '../../components/ConfirmModal';
import { cotizacionesService } from '../../services/cotizacionesService';
import { useNavigate } from 'react-router-dom';
import SnowFallComponent from 'components/SnowFallComponent';

function CotizacionDetalle({ cotizacion, onClose, usuarioId }) {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [convirtiendo, setConvirtiendo] = useState(false);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getEstatusBadge = (estatus) => {
    const estatusLower = (estatus || '').toLowerCase();
    if (estatusLower === 'completada') {
      return <Badge color="info">Completada</Badge>;
    } else if (estatusLower === 'convertida') {
      return <Badge color="success">Convertida</Badge>;
    } else if (estatusLower === 'cancelada') {
      return <Badge color="danger">Cancelada</Badge>;
    }
    return <Badge color="secondary">{estatus || 'Desconocido'}</Badge>;
  };

  // Normalizar datos PRIMERO
  const cotizacionNormalizada = {
    CotizacionID: cotizacion.CotizacionID || cotizacion.cotizacionID || cotizacion.VentaID || cotizacion.ventaID,
    SucursalNombre: cotizacion.SucursalNombre || cotizacion.sucursalNombre || '',
    ClienteNombre: cotizacion.ClienteNombre || cotizacion.clienteNombre,
    UsuarioNombre: cotizacion.UsuarioNombre || cotizacion.usuarioNombre || '',
    Folio: cotizacion.Folio || cotizacion.folio,
    Subtotal: cotizacion.Subtotal !== undefined ? parseFloat(cotizacion.Subtotal) : (cotizacion.subtotal !== undefined ? parseFloat(cotizacion.subtotal) : 0),
    DescuentoTotal: cotizacion.DescuentoTotal !== undefined ? parseFloat(cotizacion.DescuentoTotal) : (cotizacion.descuentoTotal !== undefined ? parseFloat(cotizacion.descuentoTotal) : 0),
    Impuestos: cotizacion.Impuestos !== undefined ? parseFloat(cotizacion.Impuestos) : (cotizacion.impuestos !== undefined ? parseFloat(cotizacion.impuestos) : 0),
    Total: cotizacion.Total !== undefined ? parseFloat(cotizacion.Total) : (cotizacion.total !== undefined ? parseFloat(cotizacion.total) : 0),
    Estatus: cotizacion.Estatus || cotizacion.estatus || '',
    FechaCotizacion: cotizacion.FechaCotizacion || cotizacion.fechaCotizacion || cotizacion.FechaVenta || cotizacion.fechaVenta,
    VentaID: cotizacion.VentaID || cotizacion.ventaID,
    Observaciones: cotizacion.Observaciones || cotizacion.observaciones,
    Detalles: (cotizacion.Detalles || cotizacion.detalles || []).map(det => ({
      CotizacionDetalleID: det.CotizacionDetalleID || det.cotizacionDetalleID || det.VentaDetalleID || det.ventaDetalleID,
      ProductoID: det.ProductoID || det.productoID,
      ProductoNombre: det.ProductoNombre || det.productoNombre || '',
      ProductoSKU: det.ProductoSKU || det.productoSKU || '',
      Cantidad: det.Cantidad !== undefined ? parseFloat(det.Cantidad) : (det.cantidad !== undefined ? parseFloat(det.cantidad) : 0),
      PrecioUnitario: det.PrecioUnitario !== undefined ? parseFloat(det.PrecioUnitario) : (det.precioUnitario !== undefined ? parseFloat(det.precioUnitario) : 0),
      Descuento: det.Descuento !== undefined ? parseFloat(det.Descuento) : (det.descuento !== undefined ? parseFloat(det.descuento) : 0),
      Subtotal: det.Subtotal !== undefined ? parseFloat(det.Subtotal) : (det.subtotal !== undefined ? parseFloat(det.subtotal) : 0),
      Impuestos: det.Impuestos !== undefined ? parseFloat(det.Impuestos) : (det.impuestos !== undefined ? parseFloat(det.impuestos) : 0),
      Total: det.Total !== undefined ? parseFloat(det.Total) : (det.total !== undefined ? parseFloat(det.total) : 0),
    })),
  };

  // Ahora podemos usar cotizacionNormalizada
  const handleConvertirAVenta = () => {
    // Redirigir a ventas con el folio de la cotización
    if (cotizacionNormalizada.Folio) {
      navigate(`/admin/ventas?cotizacionFolio=${cotizacionNormalizada.Folio}`);
    } else {
      // Si no tiene folio, usar el ID
      navigate(`/admin/ventas?cotizacionId=${cotizacionNormalizada.CotizacionID}`);
    }
  };

  const estatusLower = (cotizacionNormalizada.Estatus || '').toLowerCase();
  const puedeConvertir = estatusLower === 'completada' && !cotizacionNormalizada.VentaID;

  const exportarCotizacionCSV = () => {
    const folio = cotizacionNormalizada.Folio || `COT-${cotizacionNormalizada.CotizacionID}`;
    
    // Crear contenido CSV
    let csvContent = 'COTIZACIÓN\n';
    csvContent += `Folio,${folio}\n`;
    csvContent += `Fecha,${formatDate(cotizacionNormalizada.FechaCotizacion)}\n`;
    csvContent += `Cliente,${cotizacionNormalizada.ClienteNombre || 'Cliente General'}\n`;
    csvContent += `Vendedor,${cotizacionNormalizada.UsuarioNombre}\n`;
    csvContent += `Sucursal,${cotizacionNormalizada.SucursalNombre}\n`;
    csvContent += `Estatus,${cotizacionNormalizada.Estatus}\n`;
    csvContent += `Subtotal,${formatCurrency(cotizacionNormalizada.Subtotal)}\n`;
    csvContent += `Descuento Total,${formatCurrency(cotizacionNormalizada.DescuentoTotal)}\n`;
    csvContent += `Impuestos,${formatCurrency(cotizacionNormalizada.Impuestos)}\n`;
    csvContent += `Total,${formatCurrency(cotizacionNormalizada.Total)}\n`;
    if (cotizacionNormalizada.Observaciones) {
      csvContent += `Observaciones,"${cotizacionNormalizada.Observaciones.replace(/"/g, '""')}"\n`;
    }
    csvContent += '\n';
    csvContent += 'DETALLES DE PRODUCTOS\n';
    csvContent += 'Producto,SKU,Cantidad,Precio Unitario,Descuento,Impuestos,Subtotal,Total\n';
    
    cotizacionNormalizada.Detalles.forEach(det => {
      const producto = (det.ProductoNombre || '').replace(/"/g, '""');
      const sku = (det.ProductoSKU || '').replace(/"/g, '""');
      csvContent += `"${producto}","${sku}",${det.Cantidad},${det.PrecioUnitario},${det.Descuento},${det.Impuestos},${det.Subtotal},${det.Total}\n`;
    });

    // Crear blob y descargar
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `cotizacion_${folio}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="content">
      <SnowFallComponent />
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <Row>
                <Col>
                  <CardTitle tag="h4">Detalle de Cotización #{cotizacionNormalizada.CotizacionID}</CardTitle>
                </Col>
                <Col className="text-right">
                  <Button 
                    color="info" 
                    onClick={exportarCotizacionCSV} 
                    className="mr-2"
                  >
                    <i className="tim-icons icon-cloud-download-93" /> Exportar CSV
                  </Button>
                  {puedeConvertir && (
                    <Button color="success" onClick={handleConvertirAVenta} className="mr-2">
                      <i className="tim-icons icon-cart" /> Convertir a Venta
                    </Button>
                  )}
                  <Button color="secondary" onClick={onClose}>
                    <i className="tim-icons icon-simple-remove" /> Cerrar
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {error && (
                <Alert color="danger" className="mb-3">
                  {error}
                </Alert>
              )}
              {success && (
                <Alert color="success" className="mb-3">
                  {success}
                </Alert>
              )}

              <Row className="mb-4">
                <Col md="6">
                  <h6>Información General</h6>
                  <Table borderless size="sm">
                    <tbody>
                      <tr>
                        <td><strong>Folio:</strong></td>
                        <td>{cotizacionNormalizada.Folio || '-'}</td>
                      </tr>
                      <tr>
                        <td><strong>Fecha:</strong></td>
                        <td>{formatDate(cotizacionNormalizada.FechaCotizacion)}</td>
                      </tr>
                      <tr>
                        <td><strong>Sucursal:</strong></td>
                        <td>{cotizacionNormalizada.SucursalNombre}</td>
                      </tr>
                      <tr>
                        <td><strong>Cliente:</strong></td>
                        <td>{cotizacionNormalizada.ClienteNombre || 'Cliente General'}</td>
                      </tr>
                      <tr>
                        <td><strong>Vendedor:</strong></td>
                        <td>{cotizacionNormalizada.UsuarioNombre}</td>
                      </tr>
                      <tr>
                        <td><strong>Estatus:</strong></td>
                        <td>{getEstatusBadge(cotizacionNormalizada.Estatus)}</td>
                      </tr>
                      {cotizacionNormalizada.VentaID && (
                        <tr>
                          <td><strong>Venta Generada:</strong></td>
                          <td>
                            <Badge color="success">Venta #{cotizacionNormalizada.VentaID}</Badge>
                          </td>
                        </tr>
                      )}
                      {cotizacionNormalizada.Observaciones && (
                        <tr>
                          <td><strong>Observaciones:</strong></td>
                          <td>{cotizacionNormalizada.Observaciones}</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Col>
                <Col md="6">
                  <h6>Totales</h6>
                  <Table borderless size="sm">
                    <tbody>
                      <tr>
                        <td><strong>Subtotal:</strong></td>
                        <td className="text-right">{formatCurrency(cotizacionNormalizada.Subtotal)}</td>
                      </tr>
                      <tr>
                        <td><strong>Descuento Total:</strong></td>
                        <td className="text-right">{formatCurrency(cotizacionNormalizada.DescuentoTotal)}</td>
                      </tr>
                      <tr>
                        <td><strong>Impuestos:</strong></td>
                        <td className="text-right">{formatCurrency(cotizacionNormalizada.Impuestos)}</td>
                      </tr>
                      <tr>
                        <td><strong>Total:</strong></td>
                        <td className="text-right">
                          <h5>{formatCurrency(cotizacionNormalizada.Total)}</h5>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col md="12">
                  <h6>Detalles de Productos</h6>
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>Producto</th>
                        <th>SKU</th>
                        <th>Cantidad</th>
                        <th>Precio Unit.</th>
                        <th>Descuento</th>
                        <th>Impuestos</th>
                        <th>Subtotal</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cotizacionNormalizada.Detalles.map((detalle, index) => (
                        <tr key={index}>
                          <td>{detalle.ProductoNombre}</td>
                          <td>{detalle.ProductoSKU}</td>
                          <td>{detalle.Cantidad.toFixed(2)}</td>
                          <td>{formatCurrency(detalle.PrecioUnitario)}</td>
                          <td>{formatCurrency(detalle.Descuento)}</td>
                          <td>{formatCurrency(detalle.Impuestos)}</td>
                          <td>{formatCurrency(detalle.Subtotal)}</td>
                          <td>{formatCurrency(detalle.Total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Col>
              </Row>

              {puedeConvertir && (
                <Row className="mt-4">
                  <Col md="12" className="text-center">
                    <Alert color="info">
                      <p>Esta cotización puede ser convertida a una venta. Al convertirla, se validará el stock disponible y se actualizará el inventario.</p>
                      <Button color="success" onClick={handleConvertirAVenta}>
                        <i className="tim-icons icon-cart" /> Convertir a Venta
                      </Button>
                    </Alert>
                  </Col>
                </Row>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

    </div>
  );
}

export default CotizacionDetalle;

